<template>
  <div class="recorder" :class="modifiers">
    <BaseIcon
      name="microphone"
      is-bright
      class="recorder__icon"
    />
    <BaseTitle
      v-if="title"
      :text="title"
      size="l"
      class="recorder__title"
    />
    <div class="recorder__label">
      <BaseText
        v-if="text"
        :text="text"
        class="recorder__text"
      />
      <div class="recorder__timer">
        <BaseStopwatch
          ref="timer"
          :has-indicator="!isPlaying"
        />
      </div>
    </div>
    <div class="recorder__actions">
      <BaseButton
        v-for="(item, i) in actions"
        :text="item.text"
        :key="`action_${i}`"
        :is-outlined="item.isOutlined"
        class="recorder__action"
        @click="item.action"
      />
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/BaseIcon.vue';
import BaseTitle from '@/components/BaseTitle.vue';
import BaseText from '@/components/BaseText.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseStopwatch from '@/components/BaseStopwatch.vue';

import CONSTANTS from '@/helpers/constants';

const {
  ACTION_REMAKE,
  ACTION_PLAY,
  ACTION_CONTINUE,
} = CONSTANTS;

export default {
  name: 'Recorder',
  components: {
    BaseIcon,
    BaseTitle,
    BaseText,
    BaseButton,
    BaseStopwatch,
  },
  props: {
    audio: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isPlaying: false,
      isRecording: false,
      isRecordFinish: false,
    };
  },
  staticData: () => ({
    timestamp: null,
    recordDuration: 0,
    playingTimeout: null,
  }),
  computed: {
    modifiers() {
      return {
        recorder_playing: this.isPlaying,
        recorder_recording: this.isRecording,
      };
    },
    title() {
      if (this.isRecording) {
        return this.$t('titles.recordIsStarted');
      }
      if (this.isRecordFinish) {
        return this.$t('titles.recordIsFinish');
      }
      return this.$t('titles.audioRecord');
    },
    text() {
      if (this.isPlaying || this.isRecording) {
        return '00:00';
      }
      if (this.isRecordFinish) {
        return this.$t('texts.playRecording');
      }
      return this.$t('texts.startRecording');
    },
    actions() {
      if (this.isPlaying) {
        return [
          {
            text: this.$t('actions.rewrite'),
            action: this.startNewRecording,
            isOutlined: true,
          },
          {
            text: this.$t('actions.continue'),
            action: this.sendContinue,
          },
        ];
      }
      if (this.isRecording) {
        return [
          {
            text: this.$t('actions.abort'),
            action: this.sendAbort,
            isOutlined: true,
          },
          {
            text: this.$t('actions.finish'),
            action: this.stop,
          },
        ];
      }
      if (this.isRecordFinish) {
        return [
          {
            text: this.$t('actions.rewrite'),
            action: this.startNewRecording,
            isOutlined: true,
          },
          {
            text: this.$t('actions.listen'),
            action: this.startPlaying,
          },
          {
            text: this.$t('actions.continue'),
            action: this.sendContinue,
          },
        ];
      }
      return [
        {
          text: this.$t('actions.start'),
          action: this.start,
        },
      ];
    },
  },
  mounted() {
    this.initActions();
  },
  beforeDestroy() {
    if (this.isRecording) {
      this.stop();
    }
    if (this.isPlaying) {
      this.stopPlaying();
    }
  },
  methods: {
    async start(timeout = 60) {
      try {
        this.$robot.dialogService.abortRobotReplic();
        this.$robot.dialogService.muteOn();
        await this.$robot.audioService.start(timeout);
      } catch (e) {
        this.sendError(e);
      }
    },
    stop() {
      this.onRecordStop();
      this.$robot.dialogService.muteOff();
      return this.$robot.audioService.stop();
    },
    startPlaying() {
      if (!this.audio || this.isPlaying) return;
      this.$robot.dialogService.abortRobotReplic();
      this.$robot.dialogService.externalPlay(this.audio.filename, 60);
      this.onPlayingStart();
    },
    stopPlaying() {
      this.$robot.dialogService.externalPlayStop();
      this.onPlayingStop();
    },
    onPlayingStart() {
      this.isPlaying = true;
      this.startTimer();
      this.setPlayingTimeout();
    },
    onPlayingStop() {
      this.isPlaying = false;
      this.stopTimer();
    },
    onRecordStart() {
      this.isRecording = true;
      this.isPlaying = false;
      this.isRecordFinish = false;
      this.timestamp = performance.now();
      this.startTimer();
      this.$emit('start');
    },
    onRecordStop() {
      this.isRecording = false;
      this.isRecordFinish = true;
      this.recordDuration = performance.now() - this.timestamp;
      this.stopTimer();
      this.$emit('stop');
    },
    startNewRecording() {
      if (this.isPlaying) {
        this.stopPlaying();
        this.clearPlayingTimeout();
      }
      this.start();
      this.clearAudio();
    },
    clearAudio() {
      this.$emit('audio', null);
    },
    sendAudio(fullPath) {
      const filename = `file://${fullPath}`;
      if (filename) {
        this.$emit('audio', { filename, fullPath });
      }
    },
    sendAbort() {
      this.$emit('abort');
    },
    sendContinue() {
      this.$emit('continue');
    },
    sendError(e) {
      console.error(e);
      return this.$emit('error', e);
    },
    startTimer() {
      return this.$refs.timer?.start();
    },
    stopTimer() {
      return this.$refs.timer?.stop();
    },
    setPlayingTimeout() {
      this.playingTimeout = window.setTimeout(this.stopPlaying, this.recordDuration);
    },
    clearPlayingTimeout() {
      window.clearTimeout(this.playingTimeout);
    },
    initActions() {
      const robotActions = [
        this.$robot.audioService.onAudioResponse(this.sendAudio),
        this.$robot.audioService.onRecordStart(this.onRecordStart),
      ];
      const rootActions = [
        [ACTION_REMAKE, this.startNewRecording],
        [ACTION_PLAY, this.startPlaying],
        [ACTION_CONTINUE, this.sendContinue],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        robotActions.forEach((unsubscribe) => unsubscribe());
        rootActions.forEach((action) => this.$root.$off(...action));
        this.clearPlayingTimeout();
      });
    },
  },
};
</script>

<style lang="stylus">
.recorder
  display block
  text-align center

  &__icon
    width 6em
    height @width
    margin 0 auto 1em auto

  &__label
    position relative
    margin-top 0.5em

  &__timer
    abs top left right bottom
    font-size 1.2em
    visibility hidden
    ~/_playing &
    ~/_recording &
      visibility visible

  &__text
    ~/_playing &
    ~/_recording &
      visibility hidden

  &__actions
    margin-top 2em

  &__action.button
    margin-left 0.4em
    margin-right @margin-left
</style>
